import { createFileRoute, Link } from '@tanstack/react-router';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useGuildConfig } from '../../../lib/auditQueries.js';
import {
  usePollDetail,
  usePolls,
  usePollMutations,
  usePollVotes,
  type PollStatus,
} from '../../../lib/pollsQueries.js';
import { Breadcrumbs } from '../../../components/Breadcrumbs.js';
import { LanguageSwitcher } from '../../../components/LanguageSwitcher.js';
import { ChannelPicker } from '../../../components/ChannelPicker.js';
import { MemberLabel } from '../../../components/MemberLabel.js';

export const Route = createFileRoute('/guilds/$guildId/polls')({
  component: PollsPage,
});

function barOf(count: number, total: number): string {
  const p = total > 0 ? Math.round((count / total) * 100) : 0;
  const filled = Math.round((p / 100) * 10);
  return '█'.repeat(filled) + '░'.repeat(10 - filled);
}
function pctOf(count: number, total: number): number {
  return total > 0 ? Math.round((count / total) * 100) : 0;
}

function PollsPage() {
  const { t } = useTranslation();
  const { guildId } = Route.useParams();
  const [tab, setTab] = useState<PollStatus>('open');
  const [editorOpen, setEditorOpen] = useState(false);
  const [detailId, setDetailId] = useState<string | null>(null);
  const polls = usePolls(guildId, tab);
  const m = usePollMutations(guildId);

  const items = polls.data?.pages.flatMap((p) => p.items) ?? [];

  return (
    <div className="min-h-screen bg-zinc-950 text-zinc-100">
      <Header guildId={guildId} />
      <main className="mx-auto max-w-5xl px-6 py-8">
        <div className="mb-4 flex items-center justify-between">
          <div className="flex gap-1 rounded-md border border-zinc-800 bg-zinc-900/40 p-1">
            {(['open', 'closed'] as const).map((s) => (
              <button
                key={s}
                type="button"
                onClick={() => {
                  setTab(s);
                }}
                className={`rounded px-3 py-1.5 text-sm ${
                  tab === s ? 'bg-indigo-600 text-white' : 'text-zinc-300 hover:bg-zinc-800/60'
                }`}
              >
                {t(`polls.tab.${s}`)}
              </button>
            ))}
          </div>
          <button
            type="button"
            onClick={() => {
              setEditorOpen(true);
            }}
            className="rounded-md bg-indigo-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-indigo-500"
          >
            {t('polls.new')}
          </button>
        </div>

        {polls.isLoading ? (
          <p className="text-sm text-zinc-400">{t('common.loading')}</p>
        ) : items.length === 0 ? (
          <p className="rounded-xl border border-zinc-800 bg-zinc-900/40 p-8 text-center text-sm text-zinc-500">
            {t('polls.empty')}
          </p>
        ) : (
          <div className="overflow-hidden rounded-xl border border-zinc-800">
            <table className="w-full text-sm">
              <thead className="bg-zinc-900/60 text-left text-xs uppercase tracking-wider text-zinc-500">
                <tr>
                  <th className="px-4 py-2">{t('polls.col.question')}</th>
                  <th className="px-4 py-2">{t('polls.col.mode')}</th>
                  <th className="px-4 py-2 text-right">{t('polls.col.votes')}</th>
                  <th className="px-4 py-2">{t('polls.col.closes')}</th>
                  <th className="px-4 py-2 text-right">{t('polls.col.actions')}</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-zinc-800/60">
                {items.map((p) => (
                  <tr key={p.id} className="hover:bg-zinc-900/40">
                    <td className="px-4 py-2">
                      <button
                        type="button"
                        className="text-left hover:text-indigo-400"
                        onClick={() => {
                          setDetailId(p.id);
                        }}
                      >
                        {p.question}
                      </button>
                      {p.anonymous ? <span className="ml-2 text-xs text-zinc-500">🕵️</span> : null}
                    </td>
                    <td className="px-4 py-2 text-zinc-400">{t(`polls.mode.${p.mode}`)}</td>
                    <td className="px-4 py-2 text-right tabular-nums">{p.totalVotes}</td>
                    <td className="px-4 py-2 text-xs text-zinc-400">
                      {p.closedAt
                        ? `🔒 ${new Date(p.closedAt).toLocaleString()}`
                        : p.closesAt
                          ? new Date(p.closesAt).toLocaleString()
                          : t('polls.openEnded')}
                    </td>
                    <td className="px-4 py-2">
                      <div className="flex justify-end gap-2">
                        {!p.closedAt ? (
                          <button
                            type="button"
                            disabled={m.close.isPending}
                            onClick={() => {
                              m.close.mutate(p.id);
                            }}
                            className="text-xs text-amber-400 hover:text-amber-300 disabled:opacity-50"
                          >
                            {t('polls.action.close')}
                          </button>
                        ) : null}
                        <button
                          type="button"
                          disabled={m.remove.isPending}
                          onClick={() => {
                            m.remove.mutate(p.id);
                          }}
                          className="text-xs text-rose-400 hover:text-rose-300 disabled:opacity-50"
                        >
                          {t('common.remove')}
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {polls.hasNextPage ? (
          <button
            type="button"
            onClick={() => {
              void polls.fetchNextPage();
            }}
            className="mt-3 w-full rounded-md border border-zinc-800 bg-zinc-900 px-3 py-2 text-sm text-zinc-300 hover:bg-zinc-800"
          >
            {polls.isFetchingNextPage ? t('common.loadingMore') : t('common.loadMore')}
          </button>
        ) : null}
      </main>

      {editorOpen ? (
        <CreatePollModal
          guildId={guildId}
          onClose={() => {
            setEditorOpen(false);
          }}
        />
      ) : null}
      {detailId ? (
        <PollDetailModal
          guildId={guildId}
          pollId={detailId}
          onClose={() => {
            setDetailId(null);
          }}
        />
      ) : null}
    </div>
  );
}

function CreatePollModal({ guildId, onClose }: { guildId: string; onClose: () => void }) {
  const { t } = useTranslation();
  const m = usePollMutations(guildId);
  const [channelId, setChannelId] = useState('');
  const [question, setQuestion] = useState('');
  const [options, setOptions] = useState<string[]>(['', '']);
  const [mode, setMode] = useState<'single' | 'multi'>('single');
  const [anonymous, setAnonymous] = useState(false);
  const [resultsVisibility, setResultsVisibility] = useState<'live' | 'after_close'>('live');
  const [closesAtLocal, setClosesAtLocal] = useState('');
  const [error, setError] = useState<string | null>(null);

  const cleanOptions = options.map((o) => o.trim()).filter((o) => o.length > 0);
  const canSave = channelId !== '' && question.trim() !== '' && cleanOptions.length >= 2;

  const submit = (publish: boolean): void => {
    setError(null);
    m.create.mutate(
      {
        channelId,
        question: question.trim(),
        options: cleanOptions,
        mode,
        anonymous,
        resultsVisibility,
        closesAt: closesAtLocal ? new Date(closesAtLocal).toISOString() : null,
      },
      {
        onSuccess: (poll) => {
          if (publish) m.publish.mutate(poll.id);
          else m.invalidate();
          onClose();
        },
        onError: (e) => {
          setError(e instanceof Error ? e.message : t('common.error'));
        },
      },
    );
  };

  return (
    <Modal title={t('polls.editor.title')} onClose={onClose}>
      <div className="space-y-4">
        <Field label={t('polls.editor.channel')}>
          <ChannelPicker
            guildId={guildId}
            value={channelId}
            onChange={setChannelId}
            placeholder={t('polls.editor.channelPick')}
          />
        </Field>
        <Field label={t('polls.editor.question')}>
          <input
            type="text"
            maxLength={300}
            value={question}
            onChange={(e) => {
              setQuestion(e.target.value);
            }}
            className="w-full rounded-md border border-zinc-800 bg-zinc-950 px-3 py-1.5 text-sm"
          />
        </Field>
        <Field label={t('polls.editor.options')}>
          <div className="space-y-2">
            {options.map((opt, i) => (
              <div key={i} className="flex gap-2">
                <input
                  type="text"
                  maxLength={100}
                  value={opt}
                  onChange={(e) => {
                    setOptions((prev) => prev.map((o, j) => (j === i ? e.target.value : o)));
                  }}
                  className="flex-1 rounded-md border border-zinc-800 bg-zinc-950 px-3 py-1.5 text-sm"
                />
                <button
                  type="button"
                  disabled={options.length <= 2}
                  onClick={() => {
                    setOptions((prev) => prev.filter((_, j) => j !== i));
                  }}
                  className="rounded-md border border-zinc-800 px-2 text-sm text-zinc-400 hover:bg-zinc-800 disabled:opacity-30"
                >
                  −
                </button>
              </div>
            ))}
            {options.length < 25 ? (
              <button
                type="button"
                onClick={() => {
                  setOptions((prev) => [...prev, '']);
                }}
                className="text-xs text-indigo-400 hover:text-indigo-300"
              >
                + {t('polls.editor.addOption')}
              </button>
            ) : null}
          </div>
        </Field>
        <div className="grid grid-cols-2 gap-4">
          <Field label={t('polls.editor.mode')}>
            <select
              className="w-full rounded-md border border-zinc-800 bg-zinc-950 px-2 py-1.5 text-sm"
              value={mode}
              onChange={(e) => {
                setMode(e.target.value as 'single' | 'multi');
              }}
            >
              <option value="single">{t('polls.mode.single')}</option>
              <option value="multi">{t('polls.mode.multi')}</option>
            </select>
          </Field>
          <Field label={t('polls.editor.results')}>
            <select
              className="w-full rounded-md border border-zinc-800 bg-zinc-950 px-2 py-1.5 text-sm"
              value={resultsVisibility}
              onChange={(e) => {
                setResultsVisibility(e.target.value as 'live' | 'after_close');
              }}
            >
              <option value="live">{t('polls.results.live')}</option>
              <option value="after_close">{t('polls.results.after_close')}</option>
            </select>
          </Field>
        </div>
        <div className="flex items-center gap-4">
          <label className="flex items-center gap-2 text-sm">
            <input
              type="checkbox"
              className="h-4 w-4 rounded border-zinc-700 bg-zinc-900"
              checked={anonymous}
              onChange={(e) => {
                setAnonymous(e.target.checked);
              }}
            />
            {t('polls.editor.anonymous')}
          </label>
          <Field label={t('polls.editor.closesAt')}>
            <input
              type="datetime-local"
              value={closesAtLocal}
              onChange={(e) => {
                setClosesAtLocal(e.target.value);
              }}
              className="rounded-md border border-zinc-800 bg-zinc-950 px-2 py-1 text-sm"
            />
          </Field>
        </div>
        {!canSave ? <p className="text-xs text-zinc-500">{t('polls.editor.minOptions')}</p> : null}
        {error ? <p className="text-xs text-rose-400">{error}</p> : null}
        <div className="flex justify-end gap-2 pt-1">
          <button
            type="button"
            onClick={onClose}
            className="rounded-md border border-zinc-800 bg-zinc-900 px-3 py-1.5 text-sm text-zinc-300 hover:bg-zinc-800"
          >
            {t('common.cancel')}
          </button>
          <button
            type="button"
            disabled={!canSave || m.create.isPending}
            onClick={() => {
              submit(false);
            }}
            className="rounded-md border border-zinc-700 bg-zinc-800 px-3 py-1.5 text-sm text-white hover:bg-zinc-700 disabled:opacity-50"
          >
            {t('polls.editor.save')}
          </button>
          <button
            type="button"
            disabled={!canSave || m.create.isPending}
            onClick={() => {
              submit(true);
            }}
            className="rounded-md bg-indigo-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-indigo-500 disabled:opacity-50"
          >
            {t('polls.editor.savePublish')}
          </button>
        </div>
      </div>
    </Modal>
  );
}

function PollDetailModal({
  guildId,
  pollId,
  onClose,
}: {
  guildId: string;
  pollId: string;
  onClose: () => void;
}) {
  const { t } = useTranslation();
  const detail = usePollDetail(guildId, pollId);
  const isAnon = detail.data?.poll.anonymous ?? true;
  const votes = usePollVotes(guildId, pollId, !isAnon);

  const votersByOption = new Map<string, string[]>();
  for (const v of votes.data?.votes ?? []) {
    if (!v.voterId) continue;
    const list = votersByOption.get(v.optionId) ?? [];
    list.push(v.voterId);
    votersByOption.set(v.optionId, list);
  }

  return (
    <Modal title={detail.data?.poll.question ?? t('common.loading')} onClose={onClose}>
      {detail.isLoading || !detail.data ? (
        <p className="text-sm text-zinc-400">{t('common.loading')}</p>
      ) : (
        <div className="space-y-4">
          <p className="text-xs text-zinc-500">
            {t('polls.detail.createdBy')}{' '}
            <MemberLabel guildId={guildId} userId={detail.data.poll.createdBy} compact />
          </p>
          <div className="space-y-3">
            {detail.data.options.map((o) => (
              <div key={o.id}>
                <div className="flex justify-between text-sm">
                  <span>{o.label}</span>
                  <span className="tabular-nums text-zinc-400">
                    {pctOf(o.count, detail.data!.total)}% ({o.count})
                  </span>
                </div>
                <code className="text-xs text-indigo-300">{barOf(o.count, detail.data.total)}</code>
                {!isAnon && (votersByOption.get(o.id)?.length ?? 0) > 0 ? (
                  <div className="mt-1 flex flex-wrap gap-2">
                    {votersByOption.get(o.id)!.map((uid) => (
                      <span key={uid} className="text-xs text-zinc-500">
                        <MemberLabel guildId={guildId} userId={uid} compact />
                      </span>
                    ))}
                  </div>
                ) : null}
              </div>
            ))}
          </div>
          <p className="text-xs text-zinc-500">
            {t('polls.detail.total', { count: detail.data.total })}
          </p>
        </div>
      )}
    </Modal>
  );
}

function Modal({
  title,
  onClose,
  children,
}: {
  title: string;
  onClose: () => void;
  children: React.ReactNode;
}) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4">
      <div className="max-h-[85vh] w-full max-w-lg overflow-y-auto rounded-xl border border-zinc-800 bg-zinc-950 p-6">
        <div className="mb-4 flex items-start justify-between gap-4">
          <h2 className="text-lg font-semibold">{title}</h2>
          <button type="button" onClick={onClose} className="text-zinc-400 hover:text-zinc-200">
            ✕
          </button>
        </div>
        {children}
      </div>
    </div>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div>
      <label className="mb-1 block text-xs text-zinc-400">{label}</label>
      {children}
    </div>
  );
}

function Header({ guildId }: { guildId: string }) {
  const { t } = useTranslation();
  const config = useGuildConfig(guildId);
  return (
    <header className="border-b border-zinc-800">
      <div className="mx-auto flex max-w-6xl items-center gap-3 px-6 py-4">
        <Link to="/" className="shrink-0">
          <img src="/icon.png" alt="Frankenstein" className="h-6 w-6" />
        </Link>
        <Breadcrumbs
          items={[
            { label: t('app.name'), to: '/' },
            { label: config.data?.name ?? guildId, to: '/guilds/$guildId', params: { guildId } },
            { label: t('polls.title') },
          ]}
        />
        <div className="ml-auto">
          <LanguageSwitcher />
        </div>
      </div>
    </header>
  );
}
